<template lang="">
  <div>
    <validation-observer ref="simpleRules">
      <form-wizard
        ref="wizard"
        color="#7367F0"
        :title="null"
        :subtitle="null"
        layout="vertical"
        finish-button-text="Angebot verschicken"
        back-button-text="Zurück"
        next-button-text="Weiter"
        class="wizard-vertical mb-3"
        @on-complete="showSendOfferOverview"
      >
        <tab-content title="Kundendetails">
          <OfferCustomerDetails :inquiry="inquiry" />
        </tab-content>
        <tab-content title="Lager">
          <move-in-storage-selection :inquiry="inquiry" />
        </tab-content>
        <tab-content title="Angebot">
          <move-in-make-booking :inquiry="inquiry" />
        </tab-content>
      </form-wizard>
    </validation-observer>

    <!-- Enhanced Progress Modal -->
    <b-modal
      id="modal-booking"
      centered
      :hide-footer="true"
      :hide-header="true"
      :hide-header-close="true"
      size="lg"
      :no-close-on-backdrop="true"
      :no-close-on-esc="!canCancelOperation"
    >
      <div class="text-center">
        <!-- Progress Header -->
        <div class="mb-4">
          <h5 class="mb-2">
            {{ progressData.title || 'Angebot wird versendet' }}
          </h5>
          <p class="text-muted mb-0">
            {{ progressData.message || 'Bitte haben Sie einen Moment Geduld...' }}
          </p>
        </div>

        <!-- Progress Indicator -->
        <div class="mb-4">
          <div
            v-if="progressData.type === 'indeterminate'"
            class="d-flex justify-content-center"
          >
            <b-spinner
              variant="primary"
              style="width: 3rem; height: 3rem;"
            />
          </div>
          <div v-else>
            <!-- Progress Bar -->
            <b-progress
              :value="progressData.progress"
              :max="progressData.total || 100"
              variant="success"
              striped
              animated
              height="8px"
              class="mb-2"
            />
            <p class="small text-muted mb-0">
              {{ progressData.completed || 0 }} von {{ progressData.total || 0 }} abgeschlossen
            </p>
          </div>
        </div>

        <!-- Current Status -->
        <div class="mb-4">
          <div class="d-flex align-items-center justify-content-center mb-2">
            <feather-icon
              :icon="getCurrentStatusIcon()"
              :class="`text-${getCurrentStatusVariant()}`"
              size="20"
            />
            <span class="ml-2 font-weight-medium">{{ progressData.currentStep || 'Verarbeitung läuft...' }}</span>
          </div>
          <div
            v-if="progressData.responseTime"
            class="small text-muted"
          >
            Antwortzeit: {{ progressData.responseTime }}ms
          </div>
        </div>

        <!-- Step Progress Indicators -->
        <div
          v-if="progressData.steps && progressData.steps.length > 0"
          class="mb-4"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div
              v-for="(step, index) in progressData.steps"
              :key="index"
              class="d-flex flex-column align-items-center step-indicator"
              :class="{
                'step-completed': step.status === 'completed',
                'step-active': step.status === 'active',
                'step-error': step.status === 'error'
              }"
            >
              <div class="step-icon mb-1">
                <feather-icon
                  :icon="getStepIcon(step.status)"
                  size="16"
                />
              </div>
              <small class="text-center">{{ step.label }}</small>
            </div>
          </div>
        </div>

        <!-- Error Section -->
        <div
          v-if="progressData.error"
          class="mb-4"
        >
          <div class="alert alert-danger">
            <h6 class="alert-heading">
              {{ progressData.error.title }}
            </h6>
            <p class="mb-0">
              {{ progressData.error.message }}
            </p>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-center">
          <b-button
            v-if="canCancelOperation"
            variant="outline-secondary"
            class="mr-2"
            @click="cancelOperation"
          >
            Abbrechen
          </b-button>
          <b-button
            v-if="progressData.error && progressData.canRetry"
            variant="primary"
            @click="retryOperation"
          >
            Erneut versuchen
          </b-button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="modal-booking-danger"
      ok-only
      ok-variant="danger"
      ok-title="Ok"
      modal-class="modal-danger"
      centered
      title="Upps, Fehler :("
    >
      <b-card-text>
        Im Angebotsprozess ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.
      </b-card-text>
      <!-- Button öffnet das Fehlercode-Modal -->
      <b-button
        v-b-modal.modal-booking-errorcode
        variant="danger"
      >
        Fehlercode anzeigen!
      </b-button>
    </b-modal>

    <!-- Enhanced Error Modal with Technical Details -->
    <b-modal
      id="modal-booking-errorcode"
      title="Fehlerdetails"
      hide-footer
      centered
      size="lg"
    >
      <div v-if="lastError">
        <h6>Benutzerfreundliche Nachricht:</h6>
        <div
          class="p-3 mb-3 rounded"
          :class="`alert alert-${getAlertVariant(lastError.category)}`"
        >
          <h6 class="alert-heading">
            {{ lastError.friendlyMessage.title }}
          </h6>
          <p class="mb-2">
            {{ lastError.friendlyMessage.message }}
          </p>
          <hr>
          <p class="mb-0">
            <small>{{ lastError.friendlyMessage.recovery }}</small>
          </p>
        </div>

        <h6>Technische Details:</h6>
        <div class="p-3 bg-light rounded">
          <p><strong>Zeitstempel:</strong> {{ lastError.timestamp }}</p>
          <p><strong>Kategorie:</strong> {{ lastError.category }}</p>
          <p><strong>Status:</strong> {{ lastError.technical.status || 'Unbekannt' }}</p>
          <p><strong>URL:</strong> {{ lastError.technical.url || 'Nicht verfügbar' }}</p>
          <p><strong>Nachricht:</strong> {{ lastError.technical.message }}</p>
        </div>

        <div class="mt-3 text-center">
          <b-button
            v-if="lastError.isRetryable"
            variant="primary"
            :disabled="isSendingOffer"
            @click="retryLastOperation"
          >
            <b-spinner
              v-if="isSendingOffer"
              small
              class="mr-1"
            />
            {{ isSendingOffer ? 'Wird wiederholt...' : 'Erneut versuchen' }}
          </b-button>
        </div>
      </div>
      <div v-else>
        <b-card-text>
          {{ errorcode }}
        </b-card-text>
      </div>
    </b-modal>

    <!-- Neues Modal für Angebotsübersicht & Versand -->
    <b-modal
      id="modal-send-offer"
      scrollable
      title="Finale Prüfung"
      cancel-title="Abbrechen"
      ok-variant="success"
      ok-title="Ja, Angebot verschicken"
      cancel-variant="outline-secondary"
      modal-class="modal-success"
      size="xl"
      :ok-disabled="isSendingOffer"
      @ok="sendOffer"
    >
      <!-- Zoho Service Status Indicator -->
      <div
        class="mb-4 p-3 border rounded"
        :class="{
          'border-success bg-light-success': zohoStatus.isOnline === true,
          'border-danger bg-light-danger': zohoStatus.isOnline === false,
          'border-warning bg-light-warning': zohoStatus.isOnline === null
        }"
      >
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <feather-icon
              :icon="zohoStatus.isOnline === true ? 'CheckCircleIcon' :
                zohoStatus.isOnline === false ? 'XCircleIcon' : 'ClockIcon'"
              :class="{
                'text-success': zohoStatus.isOnline === true,
                'text-danger': zohoStatus.isOnline === false,
                'text-warning': zohoStatus.isOnline === null
              }"
              size="20"
            />
            <div class="ml-2">
              <h6 class="mb-0">
                Vertragssystem:
                <span
                  :class="{
                    'text-success': zohoStatus.isOnline === true,
                    'text-danger': zohoStatus.isOnline === false,
                    'text-warning': zohoStatus.isOnline === null
                  }"
                >
                  {{ getZohoStatusText() }}
                </span>
              </h6>
              <small class="text-muted">
                {{ zohoStatus.lastChecked ?
                  `Zuletzt geprüft: ${getFormattedTime(zohoStatus.lastChecked)}` :
                  'Status wird geprüft...' }}
              </small>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <b-button
              v-if="zohoStatus.isOnline === false"
              variant="outline-primary"
              size="sm"
              :disabled="isCheckingZoho"
              @click="checkZohoStatus"
            >
              <b-spinner
                v-if="isCheckingZoho"
                small
                class="mr-1"
              />
              {{ isCheckingZoho ? 'Prüfe...' : 'Erneut prüfen' }}
            </b-button>
          </div>
        </div>
        <div
          v-if="zohoStatus.error"
          class="mt-2"
        >
          <small class="text-danger">{{ zohoStatus.error }}</small>
        </div>
      </div>

      <!-- DEV MODE Toggle (only shown in development) -->
      <div
        v-if="isDevelopment"
        class="mb-4 p-3 border rounded border-warning bg-light-warning"
      >
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <feather-icon
              icon="CodeIcon"
              class="text-warning"
              size="20"
            />
            <div class="ml-2">
              <h6 class="mb-0 text-warning">
                🚀 ENTWICKLUNGSMODUS
              </h6>
              <small class="text-muted">
                Optionen nur in der Entwicklungsumgebung verfügbar
              </small>
            </div>
          </div>
          <b-form-checkbox
            v-model="skipZohoInDev"
            class="custom-control-warning"
            switch
          >
            {{ skipZohoInDev ? 'Dummy-Daten verwenden' : 'Echte Zoho-Verträge' }}
          </b-form-checkbox>
        </div>
        <div
          v-if="skipZohoInDev"
          class="mt-2 small text-warning"
        >
          ⚠️ ACHTUNG: Es werden DUMMY-DATEN erstellt! Keine echten Zoho Sign Verträge.
        </div>
      </div>

      <div class="offer-overview p-3">
        <h4>Kundendaten</h4>
        <p>
          <strong>Name:</strong>
          {{ offer.firstName }} {{ offer.lastName }}
        </p>
        <p>
          <strong>Adresse:</strong>
          {{ offer.street }} {{ offer.streetNumber }}, {{ offer.zipCode }} {{ offer.city }}
        </p>
        <p><strong>E-Mail:</strong> {{ offer.email }}</p>
        <p><strong>Telefon:</strong> {{ offer.mobileNumber || offer.phoneNumber }}</p>
        <hr>
        <h4>Lagerinformationen</h4>
        <div v-if="offer.storages && offer.storages.length > 0">
          <b-table-lite
            :items="offer.storages"
            :fields="storageTableFields"
            responsive
            striped
            hover
            class="mb-0"
          >
            <template #cell(storageIdent)="data">
              <strong>{{ data.item.storageIdent }}</strong>
            </template>
            
            <template #cell(details)="data">
              <div>
                {{ data.item.name }}<br>
                <small class="text-muted">{{ data.item.storageSpace }} m²</small>
              </div>
            </template>
            
            <template #cell(pricing)="data">
              <div>
                <strong>{{ data.item.price.toFixed(2) }} €</strong>
                <span
                  v-if="data.item.discount > 0"
                  class="text-success"
                >
                  <br><small>-{{ data.item.discount }} € Rabatt</small>
                </span>
              </div>
            </template>
            
            <template #cell(total)="data">
              <div>
                <strong class="text-primary">{{ getStorageSumPrice(data.item) }} €</strong>
                <br><small class="text-muted">inkl. MwSt</small>
              </div>
            </template>
            
            <template #cell(period)="data">
              <div>
                <strong>Start:</strong> {{ getFormattedDate(data.item.rentStart) }}<br>
                <strong>Ende:</strong> 
                <span v-if="data.item.rentEnd">{{ getFormattedDate(data.item.rentEnd) }}</span>
                <span
                  v-else
                  class="text-muted"
                >unbefristet</span>
              </div>
            </template>
            
            <template #cell(location)="data">
              <div v-if="getLocationById(data.item.location)">
                <div>
                  {{ getLocationById(data.item.location).street }} 
                  {{ getLocationById(data.item.location).streetNumber }}
                </div>
                <small class="text-muted">
                  {{ getLocationById(data.item.location).zipCode }} 
                  {{ getLocationById(data.item.location).city }}
                </small>
              </div>
              <div
                v-else
                class="text-muted"
              >
                <small>Ort unbekannt</small>
              </div>
            </template>
          </b-table-lite>
          
          <!-- Gesamtsumme -->
          <div class="mt-3 p-3 bg-light rounded">
            <div class="row">
              <div class="col-md-6">
                <h5>Zusammenfassung</h5>
                <p class="mb-1">
                  <strong>Anzahl Lagereinheiten:</strong> {{ offer.storages.length }}
                </p>
              </div>
              <div class="col-md-6 text-md-right">
                <h5>Gesamtsumme monatlich</h5>
                <h4 class="text-primary mb-0">
                  {{ getTotalPrice() }} €
                </h4>
                <small class="text-muted">inkl. MwSt</small>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <p>Keine Lagerdaten vorhanden.</p>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import { required } from '@validations'
import MoveInStorageSelection from '@/views/pages/movein/MoveInStorageSelection.vue'
import MoveInMakeBooking from '@/views/pages/movein/MoveInMakeBooking.vue'
import Offer from '@/store/offer/Offer'
import {
  BButton,
  BModal,
  VBModal,
  BSpinner,
  BTabs,
  BCardText,
  BTab,
  BTableLite,
  BProgress,
  BSpinner as BSpinnerComponent,
  BFormCheckbox
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import VueHtml2pdf from 'vue-html2pdf'
import PreviewContractView from '@/views/pages/movein/PreviewContractView.vue'
import sendEmail from '@/service/emailService'
import zohoSignHelper from '@/helper/zohoSignHelper'
import helper from '@/helper/helper'
import storageHelper from '@/helper/storageHelper'
import emailStoragesBody from '@/helper/emailStoragesBody'
import zohoService from '@/service/zohoService'
import OfferCustomerDetails from './OfferCustomerDetails.vue'
import ContractLagermeister from '../movein/ContractLagermeister.vue'

export default {
  setup() {
    const { getFormattedDate } = helper()
    const { getStorageSumPrice } = storageHelper()
    return { getFormattedDate, getStorageSumPrice }
  },
  components: {
    OfferCustomerDetails,
    MoveInStorageSelection,
    MoveInMakeBooking,
    ValidationProvider,
    ValidationObserver,
    FormWizard,
    TabContent,
    Offer,
    BButton,
    BModal,
    VBModal,
    BSpinner,
    ToastificationContent,
    VueHtml2pdf,
    ContractLagermeister,
    PreviewContractView,
    BTabs,
    BCardText,
    BTab,
    BTableLite,
    BFormCheckbox,
    BProgress,
    BSpinnerComponent,
    emailStoragesBody
  },
  data() {
    return {
      inquiry: {
        customer: this.$route.params.booking ? this.$route.params.booking : {},
        selectedStorages: [],
        offerNumber: '',
        offerDate: '',
        contractArray: []
      },
      spinnerProgressLabel: '',
      errorcode: '',
      required,
      pdfBookingFile: null,
      pdfStoragePrintNumber: 0,
      offer: Offer,
      // Enhanced state management
      isSendingOffer: false,
      isCheckingZoho: false,
      canCancelOperation: false,
      operationCancelled: false,
      zohoServiceInstance: null,
      cancellationToken: null,
      lastError: null,
      createdOffer: null, // Store the created offer for success flow
      skipZohoInDev: true, // DEV TOGGLE: Skip Zoho integration and use dummy data
      // Zoho status tracking
      zohoStatus: {
        isOnline: null,
        lastChecked: null,
        responseTime: null,
        error: null
      },
      // Progress tracking
      progressData: {
        type: 'indeterminate', // 'indeterminate' or 'determinate'
        title: '',
        message: '',
        currentStep: '',
        progress: 0,
        total: 0,
        completed: 0,
        responseTime: null,
        error: null,
        canRetry: false,
        steps: []
      },
      storageTableFields: [
        {
          key: 'storageIdent',
          label: 'Lager-ID',
          thStyle: { width: '120px' }
        },
        {
          key: 'details',
          label: 'Details',
          thStyle: { width: '200px' }
        },
        {
          key: 'pricing',
          label: 'Basispreis',
          thStyle: { width: '120px' }
        },
        {
          key: 'total',
          label: 'Summe mtl.',
          thStyle: { width: '120px' }
        },
        {
          key: 'period',
          label: 'Mietdauer',
          thStyle: { minWidth: '200px' }
        },
        {
          key: 'location',
          label: 'Ort',
          thStyle: { minWidth: '150px' }
        }
      ]
    }
  },
  computed: {
    isDevelopment() {
      // Use runtime detection instead of build-time NODE_ENV
      return window.location.hostname.includes('dev.')
             || window.location.hostname === 'localhost'
             || window.location.hostname === '127.0.0.1'
    }
  },
  async created() {
    // Initialize Zoho service
    this.zohoServiceInstance = zohoService()
    this.checkZohoStatus()

    // Load customers to enable checking for existing ones
    if (!this.$store.getters.customerEntries?.length) {
      await this.$store.dispatch('getCustomerEntries')
    }

    // Set up cleanup on component destroy
    this.$once('hook:beforeDestroy', () => {
      if (this.zohoServiceInstance) {
        this.zohoServiceInstance.stopHealthMonitoring()
      }
    })
  },

  // Component lifecycle cleanup
  beforeDestroy() {
    if (this.zohoServiceInstance) {
      this.zohoServiceInstance.stopHealthMonitoring()
    }

    if (this.cancellationToken) {
      this.cancellationToken.cancel('Component destroyed')
    }
  },
  methods: {
    // Enhanced Zoho status management
    async checkZohoStatus() {
      this.isCheckingZoho = true
      this.zohoStatus.error = null

      try {
        const healthCheck = await this.zohoServiceInstance.performHealthCheck(false)

        this.zohoStatus = {
          isOnline: healthCheck.isHealthy,
          lastChecked: new Date(),
          responseTime: healthCheck.responseTime,
          error: healthCheck.isHealthy ? null : healthCheck.error?.friendlyMessage?.message
        }
      } catch (error) {
        this.zohoStatus = {
          isOnline: false,
          lastChecked: new Date(),
          responseTime: null,
          error: 'Service nicht erreichbar'
        }
      } finally {
        this.isCheckingZoho = false
      }
    },

    getZohoStatusText() {
      if (this.zohoStatus.isOnline === null) return 'Wird geprüft...'
      if (this.zohoStatus.isOnline === true) {
        return this.zohoStatus.responseTime
          ? `Online (${this.zohoStatus.responseTime}ms)` : 'Online'
      }
      return 'Offline'
    },

    getFormattedTime(date) {
      return new Date(date).toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },

    // Enhanced error handling
    getAlertVariant(category) {
      const variants = {
        network: 'warning',
        timeout: 'warning',
        server: 'danger',
        authorization: 'warning',
        validation: 'info',
        zoho: 'danger',
        client: 'warning',
        unknown: 'danger'
      }
      return variants[category] || 'danger'
    },

    // Progress tracking methods
    getCurrentStatusIcon() {
      if (this.progressData.error) return 'AlertCircleIcon'
      if (this.progressData.type === 'indeterminate') return 'LoaderIcon'
      return 'CheckCircleIcon'
    },

    getCurrentStatusVariant() {
      if (this.progressData.error) return 'danger'
      if (this.progressData.type === 'indeterminate') return 'primary'
      return 'success'
    },

    getStepIcon(status) {
      switch (status) {
        case 'completed': return 'CheckIcon'
        case 'active': return 'LoaderIcon'
        case 'error': return 'XIcon'
        default: return 'CircleIcon'
      }
    },

    // Operation management
    cancelOperation() {
      if (this.cancellationToken) {
        this.cancellationToken.cancel('Vorgang vom Benutzer abgebrochen')
        this.operationCancelled = true
      }
      this.resetProgressState()
      this.$bvModal.hide('modal-booking')
    },

    retryLastOperation() {
      this.$bvModal.hide('modal-booking-errorcode')
      this.sendOffer()
    },

    retryOperation() {
      this.resetProgressState()
      this.sendOffer()
    },

    resetProgressState() {
      this.isSendingOffer = false
      this.canCancelOperation = false
      this.operationCancelled = false
      this.progressData = {
        type: 'indeterminate',
        title: '',
        message: '',
        currentStep: '',
        progress: 0,
        total: 0,
        completed: 0,
        responseTime: null,
        error: null,
        canRetry: false,
        steps: []
      }
    },

    // Progress callback for Zoho service
    handleProgressUpdate(progress) {
      switch (progress.type) {
        case 'start':
          this.progressData = {
            ...this.progressData,
            type: 'determinate',
            title: 'Verträge werden versendet',
            message: progress.message,
            total: progress.total,
            completed: 0,
            steps: this.createProgressSteps(progress.total)
          }
          break

        case 'progress':
          this.progressData = {
            ...this.progressData,
            completed: progress.completed,
            progress: (progress.completed / progress.total) * 100,
            currentStep: `Vertrag ${progress.current} wird versendet...`,
            message: progress.message
          }
          this.updateProgressSteps(progress.current - 1, 'active')
          break

        case 'success':
          this.updateProgressSteps(progress.current - 1, 'completed')
          break

        case 'complete':
          this.progressData = {
            ...this.progressData,
            title: 'Erfolgreich abgeschlossen',
            message: progress.message,
            currentStep: 'Alle Verträge wurden erfolgreich versendet',
            type: 'completed'
          }
          break

        case 'error':
        case 'itemError':
          this.progressData = {
            ...this.progressData,
            error: {
              title: progress.error?.friendlyMessage?.title || 'Fehler aufgetreten',
              message: progress.error?.friendlyMessage?.message || 'Ein unerwarteter Fehler ist aufgetreten'
            },
            canRetry: progress.error?.isRetryable || false
          }
          break

        case 'retry':
          this.progressData = {
            ...this.progressData,
            currentStep: progress.message,
            message: `Warte ${progress.delay} Sekunden vor nächstem Versuch...`
          }
          break

        default:
          // Handle unknown progress types gracefully
          break
      }
    },

    createProgressSteps(total) {
      const steps = []
      for (let i = 0; i < total; i += 1) {
        steps.push({
          label: `Vertrag ${i + 1}`,
          status: 'pending'
        })
      }
      return steps
    },

    updateProgressSteps(index, status) {
      if (this.progressData.steps[index]) {
        this.progressData.steps[index].status = status
      }
    },

    getLocationById(locationId) {
      return this.$store.getters.locationById(locationId)
    },
    
    getTotalPrice() {
      if (!this.offer.storages || this.offer.storages.length === 0) {
        return '0.00'
      }
      
      const total = this.offer.storages.reduce((sum, storage) => sum + this.getStorageSumPrice(storage), 0)
      
      return total.toFixed(2)
    },
    
    updateContractArray(pdf) {
      this.inquiry.contractArray.push(pdf)
    },
    offerMapping() {
      console.log('🔍 DEBUG: Starting offerMapping, this.inquiry:', this.inquiry)
      console.log('🔍 DEBUG: Customer data:', this.inquiry.customer)

      // Validate customer email before proceeding
      if (!this.inquiry.customer.email || this.inquiry.customer.email.trim() === '') {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Fehlende E-Mail-Adresse',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Eine gültige E-Mail-Adresse ist für die Vertragserstellung erforderlich.'
          }
        })
        throw new Error('Customer email is required for contract creation')
      }

      this.offer.firstName = this.inquiry.customer.firstName
      this.offer.lastName = this.inquiry.customer.lastName
      this.offer.email = this.inquiry.customer.email.trim()
      this.offer.salutation = this.inquiry.customer.salutation
      this.offer.street = this.inquiry.customer.street
      this.offer.streetNumber = this.inquiry.customer.streetNumber
      this.offer.zipCode = this.inquiry.customer.zipCode
      this.offer.city = this.inquiry.customer.city
      this.offer.country = this.inquiry.customer.country
      this.offer.phoneNumber = this.inquiry.customer.phoneNumber
      this.offer.mobileNumber = this.inquiry.customer.mobileNumber
      this.offer.company = this.inquiry.customer.company || ''
      this.offer.offerDate = new Date().toISOString()

      // Create clean copies of storages without rent dates for offer creation
      // This preserves the original inquiry.selectedStorages for Zoho Sign which needs the dates
      console.log('🔍 DEBUG: Original inquiry.selectedStorages before cleaning:', this.inquiry.selectedStorages.map(s => ({ id: s.id, name: s.name, rentStart: s.rentStart, rentEnd: s.rentEnd })))

      this.offer.storages = this.inquiry.selectedStorages.map(storage => {
        const cleanStorage = { ...storage }
        console.log(`🔍 DEBUG: Keeping dates - Storage ${storage.id} has rentStart: ${cleanStorage.rentStart}, rentEnd: ${cleanStorage.rentEnd}`)
        return cleanStorage
      })

      console.log('🔍 DEBUG: Final this.offer.storages after cleaning:', this.offer.storages.map(s => ({ id: s.id, name: s.name, rentStart: s.rentStart, rentEnd: s.rentEnd })))

      this.offer.inquiry = this.inquiry.customer.id

      // Clean up empty string fields that might cause API issues
      // But preserve email field even if empty (required for Zoho contracts)
      Object.keys(this.offer).forEach(key => {
        if (this.offer[key] === '' && key !== 'email') {
          delete this.offer[key]
        }
      })

      // Check if customer exists by email
      const existingCustomer = this.$store.getters.customerExistByEmail(this.inquiry.customer.email)
      console.log('🔍 DEBUG: Existing customer check result:', existingCustomer)

      if (existingCustomer) {
        // Link to existing customer immediately
        this.offer.customer = existingCustomer.id
        console.log('Linking offer to existing customer:', existingCustomer.id)
      } else {
        // No customer yet - don't set the field at all (let it be undefined)
        // This way it won't be sent to the backend
        delete this.offer.customer
        console.log('No existing customer found - customer field omitted from offer')
      }

      this.offer.isActive = true
      console.log('🔍 DEBUG: Offer after mapping (cleaned):', this.offer)
    },
    // Enhanced offer overview with status check
    async showSendOfferOverview() {
      // Empty contract array and update offer data first
      this.inquiry.contractArray = []
      this.offerMapping()
      // Show modal immediately for better UX
      this.$bvModal.show('modal-send-offer')

      // Check Zoho status in background (non-blocking)
      this.checkZohoStatus().catch(error => {
        console.warn('Background Zoho status check failed:', error)
      })
    },
    // Enhanced offer sending with comprehensive error handling
    async sendOffer() {
      if (this.isSendingOffer) return

      // Reset state
      this.resetProgressState()
      this.isSendingOffer = true
      this.canCancelOperation = true
      this.lastError = null

      try {
        this.$bvModal.hide('modal-send-offer')

        // Step 1: Create offer and reserve storages FIRST (most important part)
        this.progressData = {
          type: 'indeterminate',
          title: 'Angebot wird erstellt...',
          message: 'Angebot wird in der Datenbank gespeichert...',
          currentStep: 'Erstelle Angebot und reserviere Lager...'
        }
        this.$bvModal.show('modal-booking')

        // Create offer and reserve storages (critical operation)
        const createdOffer = await this.formSubmitted()
        console.log('Offer created and storages reserved successfully')

        // Store the created offer for use in success flow
        this.createdOffer = createdOffer

        // Step 2: Send Zoho contracts (optional operation)
        try {
          this.progressData = {
            ...this.progressData,
            title: 'Verträge werden versendet...',
            message: 'Zoho Sign Verträge werden erstellt...',
            currentStep: 'Verbindung zu Zoho Sign wird hergestellt...'
          }

          // Check if we're in development environment and should skip Zoho
          // Use runtime detection instead of build-time NODE_ENV
          const isRuntimeDevelopment = window.location.hostname.includes('dev.')
                                     || window.location.hostname === 'localhost'
                                     || window.location.hostname === '127.0.0.1'

          if (isRuntimeDevelopment && this.skipZohoInDev) {
            console.log('🚀 DEV MODE: Skipping Zoho integration, using dummy data instead')
            await this.createDummyZohoContracts()
          } else {
            // Create cancellation token for Zoho operations
            this.cancellationToken = this.zohoServiceInstance.createCancellationToken()

            // Send contracts via Zoho
            await this.sendOfferToZohoSign()
          }
        } catch (zohoError) {
          console.warn('Zoho contract sending failed, but offer was created successfully:', zohoError)

          // Show success for offer creation even if Zoho fails
          this.$bvModal.hide('modal-booking')
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Angebot erstellt, aber Verträge konnten nicht versendet werden',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
              text: 'Das Angebot wurde erfolgreich erstellt und die Lager wurden reserviert. Die Verträge konnten jedoch nicht automatisch versendet werden.'
            }
          })

          // Finish the flow without Zoho contracts
          this.finishOfferCreation()
        }
      } catch (error) {
        console.error('Offer creation failed:', error)
        this.handleSendOfferError(error)
      }
    },

    // Finish offer creation flow (used when Zoho succeeds or fails)
    finishOfferCreation() {
      this.isSendingOffer = false
      this.canCancelOperation = false

      // Call success next steps with the created offer
      if (this.createdOffer) {
        this.successNextSteps(this.createdOffer)
      } else {
        // Fallback - just navigate to offers list
        this.$router.push({ name: 'offers-list' })
      }
    },

    // DEV MODE: Create dummy Zoho contracts instead of real ones
    async createDummyZohoContracts() {
      try {
        console.log('🚀 DEV MODE: Creating dummy Zoho contracts...')
        console.log('🔍 DEBUG: Full this.offer object:', this.offer)
        console.log('🔍 DEBUG: this.createdOffer object:', this.createdOffer)
        console.log('🔍 DEBUG: this.offer.storages:', this.offer.storages)
        console.log('🔍 DEBUG: this.inquiry.selectedStorages:', this.inquiry.selectedStorages)

        // Validate that we have the required data
        if (!this.createdOffer || !this.createdOffer.id) {
          throw new Error('createdOffer is missing or has no ID')
        }

        if (!this.createdOffer.customer) {
          throw new Error('createdOffer.customer is missing')
        }

        console.log('📋 Available data:', {
          createdOfferId: this.createdOffer?.id,
          createdOfferType: typeof this.createdOffer?.id,
          customerId: this.createdOffer.customer,
          customerIdType: typeof this.createdOffer.customer,
          storagesCount: this.inquiry.selectedStorages?.length || 0,
          offerStoragesCount: this.offer.storages?.length || 0
        })

        this.progressData = {
          ...this.progressData,
          title: '🚀 DEV MODE: Dummy Verträge werden erstellt...',
          message: '⚠️ ACHTUNG: Dies sind DUMMY-DATEN für die Entwicklung!',
          currentStep: 'Erstelle Dummy-Zoho-Verträge...'
        }

        // Use inquiry.selectedStorages as source of truth instead of this.offer.storages
        const storagesForContracts = this.inquiry.selectedStorages || []

        if (!storagesForContracts || storagesForContracts.length === 0) {
          console.warn('❌ DEV MODE: No storages found for dummy contract creation')
          return
        }

        // Create dummy contracts for each storage using Promise.all
        const dummyContractPromises = storagesForContracts.map(async (storage, index) => {
          const timestamp = Date.now() + index // Add index to ensure uniqueness

          // Ensure all IDs are integers, not objects
          const offerId = parseInt(this.createdOffer.id, 10)
          const storageId = parseInt(storage.id, 10)
          const customerId = parseInt(this.createdOffer.customer, 10)

          console.log(`🔍 DEBUG: ID types for contract ${index + 1}:`, {
            offerId,
            offerIdType: typeof offerId,
            storageId,
            storageIdType: typeof storageId,
            customerId,
            customerIdType: typeof customerId
          })

          const dummyContract = {
            offer: offerId,
            storage: storageId,
            customer: customerId,
            zohoDocumentId: `DUMMY_DOC_${timestamp}_${storage.id}`,
            zohoRequestId: `DUMMY_REQ_${timestamp}_${storage.id}`,
            zohoSignStatus: 'sent',
            zohoSignUrl: `https://dummy-zoho-url.com/sign/${storage.id}`,
            zohoSentDate: new Date().toISOString(),
            zohoSignerEmail: this.offer.email,
            zohoSignerName: `${this.offer.firstName || ''} ${this.offer.lastName || ''}`.trim(),
            zohoActionId: `DUMMY_ACTION_${timestamp}_${storage.id}`
          }

          console.log(`🚀 DEV MODE: Creating dummy contract ${index + 1}/${storagesForContracts.length} for storage ${storage.storageIdent}:`, dummyContract)

          try {
            // Store in ZohoContracts table
            const createdContract = await this.$store.dispatch('zohoContract/createZohoContract', dummyContract)
            console.log(`✅ DEV MODE: Successfully created dummy contract ${createdContract.id} for storage ${storage.storageIdent}`)
            return createdContract
          } catch (error) {
            console.error(`❌ DEV MODE: Failed to create dummy contract for storage ${storage.storageIdent}:`, error)
            console.error('❌ DEV MODE: Error details:', {
              message: error.message,
              response: error.response?.data,
              status: error.response?.status,
              contractData: dummyContract
            })
            throw error
          }
        })

        // Wait for all dummy contracts to be created
        console.log('⏳ DEV MODE: Waiting for all dummy contracts to be created...')
        const dummyContracts = await Promise.all(dummyContractPromises)
        console.log(`🎉 DEV MODE: Successfully created ${dummyContracts.length} dummy contracts`)

        // Show success message with dummy data warning
        setTimeout(() => {
          this.$bvModal.hide('modal-booking')
          this.$toast({
            component: ToastificationContent,
            props: {
              title: `🚀 DEV MODE: ${dummyContracts.length} DUMMY-Verträge erstellt!`,
              icon: 'AlertTriangleIcon',
              variant: 'warning',
              text: '⚠️ ACHTUNG: Dies sind DUMMY-DATEN für die Entwicklung! Keine echten Zoho Sign Verträge.'
            }
          })

          // Finish the offer creation flow
          this.finishOfferCreation()
        }, 1500) // Shorter delay for dev mode

        console.log(`✅ DEV MODE: Successfully created ${dummyContracts.length} dummy contracts`)
      } catch (error) {
        console.error('❌ DEV MODE: Error creating dummy contracts:', error)
        throw error
      }
    },
    // Enhanced Zoho Sign integration with comprehensive error handling
    async sendOfferToZohoSign() {
      try {
        // Validate email before attempting to send contracts
        if (!this.offer.email || this.offer.email.trim() === '') {
          throw new Error('Eine gültige E-Mail-Adresse ist für die Vertragserstellung erforderlich.')
        }

        // Use original inquiry.selectedStorages (which still have rentStart/rentEnd) for Zoho contracts
        // Enhance storages with location data for better contract payload
        const enhancedStorages = this.inquiry.selectedStorages.map(storage => ({
          ...storage,
          locationAddress: this.getLocationById(storage.location)
            ? `${this.getLocationById(storage.location).street} ${this.getLocationById(storage.location).streetNumber}, ${this.getLocationById(storage.location).zipCode} ${this.getLocationById(storage.location).city}`
            : 'Adresse nicht verfügbar',
          finalPrice: this.getStorageSumPrice(storage)
        }))

        // Use enhanced Zoho service
        const result = await this.zohoServiceInstance.sendMultipleContracts(
          enhancedStorages,
          this.offer,
          this.handleProgressUpdate.bind(this),
          this.cancellationToken
        )

        // Store Zoho response data in ZohoContracts table
        await this.storeZohoResponseData(result, enhancedStorages)

        // Success handling
        setTimeout(() => {
          this.$bvModal.hide('modal-booking')
          this.$toast({
            component: ToastificationContent,
            props: {
              title: `${result.contractsSent} Verträge erfolgreich verschickt!`,
              icon: 'CheckCircleIcon',
              variant: 'success'
            }
          })

          // Finish the offer creation flow
          this.finishOfferCreation()
        }, 2000) // Show success state briefly
      } catch (error) {
        console.error('❌ Zoho contract sending failed:', error)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Vertragssendung fehlgeschlagen',
            icon: 'XCircleIcon',
            variant: 'danger',
            text: error.message || 'Ein unerwarteter Fehler ist bei der Vertragserstellung aufgetreten.'
          }
        })

        // Don't prevent offer creation, just log the contract failure
        console.log('🟡 Continuing with offer creation despite contract failure')
      }
    },

    // Store Zoho response data in ZohoContracts table
    async storeZohoResponseData(result, enhancedStorages) {
      try {
        console.log('📦 storeZohoResponseData called with:', {
          result: result ? 'provided' : 'missing',
          resultKeys: result ? Object.keys(result) : [],
          enhancedStorages: enhancedStorages ? enhancedStorages.length : 0
        })

        if (!result.results || result.results.length === 0) {
          console.warn('❌ No Zoho results to store - result.results is empty or missing')
          console.log('🔍 Full result object:', result)
          return
        }

        console.log(`📄 Processing ${result.results.length} Zoho responses`)

        // Use the statically imported helper
        const { extractZohoContractData } = zohoSignHelper()
        console.log('✅ Helper function imported successfully')

        // Filter out undefined responses and process only valid ones
        const validResponses = result.results
          .map((response, index) => ({ response, index }))
          .filter(item => item.response !== undefined)

        console.log(`📄 Found ${validResponses.length} valid responses out of ${result.results.length} total`)

        // Process all valid Zoho responses sequentially
        const contractPromises = validResponses.map(async ({ response: zohoResponse, index }) => {
          const storage = enhancedStorages[index]

          console.log(`📋 Processing contract ${index + 1}/${result.results.length} for storage:`, {
            storageId: storage?.id,
            storageIdent: storage?.storageIdent,
            zohoResponse: zohoResponse ? 'provided' : 'missing',
            zohoSuccess: zohoResponse?.success
          })

          // Add null check for zohoResponse before accessing properties
          if (!zohoResponse || !zohoResponse.success || !zohoResponse.data) {
            console.warn(`❌ Invalid Zoho response for storage ${storage?.id || storage?.storageIdent}`)
            console.log('🔍 Full zohoResponse:', zohoResponse)
            return null
          }

          // Extract contract data using helper with proper offer ID
          const contractData = extractZohoContractData(
            zohoResponse,
            this.createdOffer.id, // Use createdOffer.id instead of offer.id
            storage.id,
            this.createdOffer.customer,
            this.offer
          )

          if (!contractData) {
            console.warn(`❌ Could not extract contract data for storage ${storage.storageIdent}`)
            return null
          }

          console.log(`💾 Creating ZohoContract for storage ${storage.storageIdent}:`, contractData)

          try {
            // Create ZohoContract record
            const createdContract = await this.$store.dispatch('zohoContract/createZohoContract', contractData)
            console.log(`✅ Successfully created ZohoContract ${createdContract.id} for storage ${storage.storageIdent}`)
            return { storage: storage.storageIdent, contractId: createdContract.id, success: true }
          } catch (contractError) {
            console.error(`❌ Failed to create ZohoContract for storage ${storage.storageIdent}:`, contractError)
            return null
          }
        })

        // Wait for all contract creation to complete
        const results = await Promise.all(contractPromises)
        const successfulResults = results.filter(result => result !== null)
        console.log(`🎉 Successfully created ${successfulResults.length} out of ${validResponses.length} ZohoContract records`)

        // Refresh ZohoContracts data
        console.log('🔄 Refreshing ZohoContracts store data...')
        await this.$store.dispatch('zohoContract/fetchZohoContracts')

        console.log(`✅ Successfully stored Zoho data for ${result.contractsSent || result.results.length} contracts in ZohoContracts table`)
      } catch (error) {
        console.error('❌ Error storing Zoho response data:', error)
        console.error('📋 Error details:', {
          message: error.message,
          stack: error.stack
        })
        // Don't throw - we don't want to break the success flow
      }
    },

    // Enhanced error handling
    handleSendOfferError(error) {
      this.isSendingOffer = false
      this.canCancelOperation = false

      // Process error with enhanced error handler
      this.lastError = this.zohoServiceInstance?.handleApiError
        ? this.zohoServiceInstance.handleApiError(error, 'Angebot versenden')
        : {
          category: 'unknown',
          friendlyMessage: {
            title: 'Unbekannter Fehler',
            message: error.message || 'Ein unerwarteter Fehler ist aufgetreten.',
            recovery: 'Versuchen Sie es erneut oder kontaktieren Sie den Support.'
          },
          technical: {
            message: error.message,
            status: error.response?.status,
            url: error.config?.url
          },
          isRetryable: true,
          timestamp: new Date().toISOString()
        }

      this.errorcode = error.message || 'Unbekannter Fehler'

      this.$bvModal.hide('modal-booking')
      this.$bvModal.show('modal-booking-danger')
    },

    async formSubmitted() {
      try {
        // Create a clean copy of the offer for API submission (storage IDs only)
        const offerForAPI = {
          ...this.offer,
          storages: this.offer.storages.map(storage => storage.id)
        }

        // Debug: Log the offer object before sending
        console.log('🔍 DEBUG: Offer object before sending:', JSON.stringify(offerForAPI, null, 2))
        console.log('🔍 DEBUG: Storage objects for reservation:', this.offer.storages)

        // Create the offer first (send only storage IDs)
        const offer = await this.$store.dispatch('addOffer', offerForAPI)
        console.log('Offer created:', offer)

        // Reserve storages immediately after offer creation (use full storage objects)
        await this.reserveOfferStorages(offer)

        console.log('Offer creation and storage reservation completed successfully')
        return offer
      } catch (error) {
        console.log('Offer creation failed:', error)
        throw error // Let the caller handle the error
      }
    },

    // Separate method for storage reservation - called immediately after offer creation
    async reserveOfferStorages(offer) {
      try {
        // Refresh storage entries to get latest data
        await this.$store.dispatch('getStorageEntries')

        // Use inquiry.selectedStorages as source of truth (same as Zoho contract creation)
        const storagesForReservation = this.inquiry.selectedStorages || []
        console.log('🔍 DEBUG: Reserving storages:', storagesForReservation.length, 'storages')

        // Reserve each storage associated with the offer
        const reservationPromises = storagesForReservation.map(async storage => {
          const storageById = this.$store.getters.storageById(storage.id)
          if (storageById) {
            storageById.storageReservation = true
            storageById.rentStart = storage.rentStart
            storageById.rentEnd = storage.rentEnd

            console.log(`Reserving storage ${storage.storageIdent} (ID: ${storage.id})`)
            await this.$store.dispatch('updateStorage', storageById)
          } else {
            console.warn(`Storage with ID ${storage.id} not found for reservation`)
          }
        })

        // Wait for all reservations to complete
        await Promise.all(reservationPromises)

        // Refresh data stores after all reservations
        await this.$store.dispatch('getStorageEntries')
        await this.$store.dispatch('getOfferEntries')

        console.log(`Successfully reserved ${storagesForReservation.length} storages for offer ${offer.id}`)
      } catch (error) {
        console.error('Error reserving storages:', error)
        // Don't throw - storage reservation failure shouldn't block offer creation
      }
    },
    successNextSteps(offer) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Angebot erfolgreich erstellt!',
          icon: 'EditIcon',
          variant: 'success'
        }
      })
      this.onSendEmail(offer)
      this.$bvModal.hide('modal-booking')
      this.$router.push({ name: 'offers-list' })
    },
    failureNextSteps(error) {
      console.log('Angebot erstellen klappt nicht! Fehlercode:', error)
      this.errorcode = error
      this.$bvModal.show('modal-booking-danger')
    },
    async onSendEmail(offer) {
      // Check if offer is valid before proceeding
      if (!offer || !offer.storages) {
        console.error('Cannot send email: Invalid offer data', offer)
        return
      }

      const emailType = 'offer'
      const emailBody = emailStoragesBody(this.inquiry.selectedStorages, emailType)
      const email = {
        to: offer.email,
        subject: 'Der Lagermeister',
        text: emailBody,
        attachments: this.inquiry.contractArray,
        template_id: 'd-5fa2a6f835f5410f8a68037a9b212982',
        dynamic_template_data: {
          salutation: offer.salutation,
          lastName: offer.lastName,
          offerDate: this.getFormattedDate(offer.offerDate),
          offerID: offer.id,
          offerItems: emailBody
        }
      }
      const result = await sendEmail(email)
      /* eslint-disable no-alert */
      if (result.success) {
        // alert(result.message)
        console.log('Email sent:', result.message)
      } else {
        // alert(result.message)
        console.error('Email not sent:', result.message)
      }
      /* eslint-enable no-alert */
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-wizard.scss';

// Enhanced CreateOffer styles
.step-indicator {
  .step-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f8f9fa;
    border: 2px solid #dee2e6;
    color: #6c757d;
    transition: all 0.3s ease;
  }

  &.step-completed .step-icon {
    background-color: #28a745;
    border-color: #28a745;
    color: white;
  }

  &.step-active .step-icon {
    background-color: #007bff;
    border-color: #007bff;
    color: white;
    animation: pulse 2s infinite;
  }

  &.step-error .step-icon {
    background-color: #dc3545;
    border-color: #dc3545;
    color: white;
  }

  small {
    max-width: 80px;
    font-size: 0.75rem;
    line-height: 1.2;
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(0, 123, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 123, 255, 0);
  }
}

// Zoho status indicator styles
.bg-light-success {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-light-danger {
  background-color: rgba(220, 53, 69, 0.1);
}

.bg-light-warning {
  background-color: rgba(255, 193, 7, 0.1);
}

// Enhanced progress modal styles
.modal-lg .modal-body {
  padding: 2rem;
}

// Enhanced error modal styles
.alert {
  border: none;
  border-radius: 0.5rem;

  &.alert-success {
    background-color: rgba(40, 167, 69, 0.1);
    color: #155724;
  }

  &.alert-danger {
    background-color: rgba(220, 53, 69, 0.1);
    color: #721c24;
  }

  &.alert-warning {
    background-color: rgba(255, 193, 7, 0.1);
    color: #856404;
  }

  &.alert-info {
    background-color: rgba(23, 162, 184, 0.1);
    color: #0c5460;
  }
}

// Progress bar enhancements
.progress {
  border-radius: 0.5rem;
  box-shadow: inset 0 0.1rem 0.2rem rgba(0, 0, 0, 0.1);
}

// Button loading state
.btn {
  &:disabled {
    cursor: not-allowed;
  }
}

// Font weight utilities
.font-weight-medium {
  font-weight: 500;
}

// Animation for status icons
.feather-icon {
  transition: all 0.2s ease;
}
</style>
